import "@mantine/core/styles.css"
import "@mantine/notifications/styles.css"
import "mantine-datatable/styles.css"

import { createTheme, MantineProvider, virtualColor } from "@mantine/core"
import { lazy, StrictMode, Suspense } from "react"
import { createRoot } from "react-dom/client"
import { createBrowserRouter, RouterProvider } from "react-router-dom"

import E404 from "./Pages/Error/E404"

import { ModalsProvider } from "@mantine/modals"
import { Notifications } from "@mantine/notifications"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Splash } from "./Commons/Renders"

import App from "./App"
import "./i18n/config"
import "./main.css"

// Lazy-loaded pages
const Home = lazy(() => import("./Pages/Home/Home"))
const AuroraStore = lazy(() => import("./Pages/Apps/AuroraStore/AuroraStore"))
const ConfigGenerator = lazy(() => import("./Pages/ConfigGenerator/Page"))
const Downloads = lazy(() => import("./Pages/Downloads/Downloads"))
const Team = lazy(() => import("./Pages/Team/Team"))
const Wiki = lazy(() => import("./Pages/Wiki/Wiki"))
const Community = lazy(() => import("./Pages/Community/Community"))

const router = createBrowserRouter(
  [
    {
      element: <App />,
      errorElement: <E404 />,
      children: [
        { path: "/", element: <Home /> },
        { path: "/aurora-store", element: <AuroraStore /> },
        { path: "/config-generator", element: <ConfigGenerator /> },
        { path: "/files/*", element: <Downloads /> },
        { path: "/team", element: <Team /> },
        { path: "/wiki", element: <Wiki /> },
        { path: "/community", element: <Community /> }
      ]
    }
  ],
  { basename: import.meta.env.BASE_URL || "/" }
)

const theme = createTheme({
  defaultRadius: "md",
  autoContrast: true,
  luminanceThreshold: 0.4,
  primaryShade: {
    light: 5,
    dark: 6
  },
  fontSizes: {
    xs: "0.75rem",
    sm: "0.80rem",
    md: "0.95rem",
    lg: "1.25rem",
    xl: "1.5rem"
  },
  lineHeights: {
    xs: "1.2",
    sm: "1.3",
    md: "1.4",
    lg: "1.5",
    xl: "1.6"
  },
  colors: {
    dark: [
      "#f4f4f5", // 0 - Primary text on darkest backgrounds
      "#e4e4e7", // 1 - Secondary text, lighter UI elements
      "#d4d4d8", // 2 - Tertiary text, placeholders, muted labels
      "#52525c", // 3 - Quaternary text, disabled state
      "#3f3f46", // 4 - Divider lines, subtle borders
      "#27272a", // 5 - Hover states, active elements
      "#2a2a2f", // 6 - Elevated surfaces, cards, menu
      "#1a1a1e", // 7 - Primary background
      "#09090b", // 8 - App shell background
      "#000000" // 9 - True black or deepest background
    ],
    primary: virtualColor({
      name: "primary",
      dark: "lime",
      light: "blue"
    })
  }
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      networkMode: "online",
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 60 // 1 hour
    }
  }
})

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="light">
      <ModalsProvider>
        <Notifications />
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<Splash />}>
            <RouterProvider router={router} />
          </Suspense>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  </StrictMode>
)
